import { CollectionItem } from 'types/Collection'
import { OutputStructure } from 'types/OutputStructure'

// Compresses an OutputStructure into a string that can be used as a system prompt
// e.g.
// {
//   emoji: {
//     type: 'string'
//     description: 'Emoji'
//   },
// }
// becomes
// {
//   e: string // Emoji
// }
export const stringifyOutputStructure = (
  obj: OutputStructure,
  usedPrefixes: Set<string> = new Set(),
  indent = ''
): string => {
  let str = '{\n'
  for (const key in obj) {
    let prefix = ''
    for (let i = 0; i < key.length; i++) {
      prefix += key[i]
      if (!usedPrefixes.has(prefix)) {
        usedPrefixes.add(prefix)
        break
      }
    }
    const prop = obj[key]
    if (prop.type === 'object' && prop.properties) {
      str += `${indent}  ${prefix}: ${stringifyOutputStructure(
        prop.properties,
        new Set(),
        indent + '  '
      )}\n`
    } else if (prop.type === 'array' && prop.properties) {
      str += `${indent}  ${prefix}: [${stringifyOutputStructure(
        prop.properties,
        new Set(),
        indent + '  '
      )}]\n`
    } else {
      str += `${indent}  ${prefix}: ${prop.type}${
        prop.description ? ' // ' + prop.description : ''
      }\n`
    }
  }
  str += `${indent}}`
  return str
}

// Expands a compressed object into a full object using the OutputStructure
// e.g.
// {
//   e: '🎉'
// }
// becomes
// {
//   emoji: '🎉'
// }
export const expandOutputKeys = (
  compressed: Record<string, any>,
  structure: OutputStructure,
  usedPrefixes: Set<string> = new Set()
): Record<string, any> => {
  const expanded: Record<string, any> = {}
  for (const key in compressed) {
    const originalKey = Object.keys(structure).find(
      (k) =>
        k.startsWith(key) &&
        !Array.from(usedPrefixes).some((prefix) => k.startsWith(prefix))
    )
    if (!originalKey) {
      continue
    }
    usedPrefixes.add(originalKey)
    const prop = compressed[key]
    if (typeof prop === 'object' && prop !== null && !Array.isArray(prop)) {
      expanded[originalKey] = expandOutputKeys(
        prop,
        structure[originalKey]?.properties || {},
        new Set()
      )
    } else if (Array.isArray(prop)) {
      expanded[originalKey] = prop.map((p) =>
        expandOutputKeys(p, structure[originalKey]?.properties || {}, new Set())
      )
    } else {
      expanded[originalKey] = prop
    }
  }
  return expanded
}

/**
 * Sanitize an item by merging it with default values
 * @param item
 * @param defaultValues
 * @returns
 */
export const sanitizeItem = <T extends CollectionItem>(
  item: T,
  defaultValues: Partial<T>
): T => {
  return {
    ...defaultValues,
    ...item,
    metadata: {
      ...defaultValues.metadata,
      ...item.metadata,
    },
  }
}
